import {
    DndContext,
    MouseSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useDebounced } from '../../hooks/useDebounced'
import { useEnableNotificationsPrompt } from '../../hooks/useEnableNotificationsPrompt'
import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { Block } from '../../models/Block'
import { Bookable, getBookablesByDate } from '../../models/Bookable'
import { Booking } from '../../models/Booking'
import { CalendarFilter, CalendarFilterType } from '../../models/CalendarFilter'
import { Lesson } from '../../models/Lesson'
import { Order } from '../../models/Order'
import { PAYEE_TYPE } from '../../models/Payee'
import { Payment } from '../../models/Payment'
import { RecordFactory } from '../../models/RecordFactory'
import { Task } from '../../models/Task'
import { useNotificationListener } from '../../providers/NotificationsManagerProvider'
import { Result, okResult } from '../../types/utils'
import {
    CALENDAR_MARGIN,
    CALENDAR_PADDING,
    CalendarView,
    DASHBOARD_BANNER_HEIGHT,
    DASHBOARD_BANNER_MARGIN,
    FILTER_ROW_HEIGHT,
    FILTER_ROW_MARGIN,
    HEADER_BAR_HEIGHT,
    calendarViews,
    emptyAvailabilityFilter,
    getCalendarStartAndEnd,
} from '../../utils/calendar'
import { SESSION_STORAGE_DISMISS_CALENDAR_BANNER } from '../../utils/constants'
import { DATE_SHORT_ISO_FORMAT, dateFromDateString } from '../../utils/dates'
import { Icon } from '../Icon'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import Banner from '../app/design/Banner'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { CalendarAgenda } from './CalendarAgenda'
import { CalendarAvailability } from './CalendarAvailability'
import { CalendarDay } from './CalendarDay'
import { CalendarFilterTags } from './CalendarFilterTags'
import { CalendarHeaderBar } from './CalendarHeaderBar'
import { CalendarMonth } from './CalendarMonth'
import { CalendarSearch } from './CalendarSearch'
import { CalendarWeek } from './CalendarWeek'

export const SchedulePage = () => {
    useEnableNotificationsPrompt()

    const { logAndDisplayError } = useErrorHandler()
    const [searchParams, setSearchParams] = useSearchParams()

    usePageTitle('Calendar')

    const currentDateParam = searchParams.get('date')
    const currentViewParam = searchParams.get('view')
    const filterProsParam = searchParams.get('pros')
    const filterClassesParam = searchParams.get('classes')
    const filterServicesParam = searchParams.get('services')
    const filterReservationsParam = searchParams.get('reservations')

    const currentDate = useMemo(() => dateFromDateString(currentDateParam), [currentDateParam])
    const currentView = useMemo(() => {
        const view =
            !!currentViewParam && calendarViews.includes(currentViewParam as CalendarView)
                ? currentViewParam
                : 'week'
        return view as CalendarView
    }, [currentViewParam])

    const filter = useMemo(() => {
        const newFilter = new CalendarFilter()

        filterProsParam?.split(',').forEach((id) => {
            newFilter.addIdByType(id, CalendarFilterType.Pros)
        })
        filterClassesParam?.split(',').forEach((id) => {
            newFilter.addIdByType(id, CalendarFilterType.Classes)
        })
        filterServicesParam?.split(',').forEach((id) => {
            newFilter.addIdByType(id, CalendarFilterType.Services)
        })
        filterReservationsParam?.split(',').forEach((id) => {
            newFilter.addIdByType(id, CalendarFilterType.Reservations)
        })

        return newFilter
    }, [filterProsParam, filterClassesParam, filterServicesParam, filterReservationsParam])

    const setCurrentDate = (newDate: moment.Moment) => {
        setSearchParams((previousParams) => {
            previousParams.set('date', newDate.format(DATE_SHORT_ISO_FORMAT))
            return previousParams
        })
    }

    const setCurrentView = (newView: CalendarView) => {
        setSearchParams((previousParams) => {
            previousParams.set('view', newView)
            return previousParams
        })
    }

    const setFilter = (newFilter: CalendarFilter) => {
        setSearchParams((previousParams) => {
            if (newFilter.hasPeople) previousParams.set('pros', newFilter.pros.join())
            else previousParams.delete('pros')

            if (newFilter.classes.length > 0)
                previousParams.set('classes', newFilter.classes.join())
            else previousParams.delete('classes')

            if (newFilter.services.length > 0)
                previousParams.set('services', newFilter.services.join())
            else previousParams.delete('services')

            if (newFilter.reservations.length > 0)
                previousParams.set('reservations', newFilter.reservations.join())
            else previousParams.delete('reservations')

            return previousParams
        })
    }

    const [availabilityFilter, setAvailabilityFilter] = useState(emptyAvailabilityFilter)
    const [showBanner, setShowBanner] = useState(false)

    const { data: isCalendarSynced } = useQuery({
        queryKey: ['calendar', 'external'],
        queryFn: async () => {
            const events = await RecordFactory.getInstance().loadEvents()
            const calendars = await RecordFactory.getInstance().loadCalendars()
            return events.length > 0 || calendars.length > 0
        },
    })

    const { data: dashboard, refetch: reloadDashboard } = useQuery({
        queryKey: ['calendar', 'dashboard'],
        queryFn: async () => {
            return (await RecordFactory.getInstance().loadDashboard('alerts_credential')) || null
        },
    })

    useEffect(() => {
        const didDismissBanner = sessionStorage.getItem(SESSION_STORAGE_DISMISS_CALENDAR_BANNER)
        if (isCalendarSynced && !dashboard) reloadDashboard()
        setShowBanner((!isCalendarSynced || !!dashboard) && !didDismissBanner)
    }, [isCalendarSynced, dashboard])

    const {
        data: items,
        isFetching: isLoadingItems,
        refetch: reloadItems,
    } = useQuery({
        queryKey: ['calendar', 'items'],
        queryFn: async () => {
            const owner = PSApplication.owner()
            return !!owner.id ? await RecordFactory.getInstance().loadItems(owner.id) : []
        },
    })

    const {
        data: pros,
        isFetching: isLoadingPros,
        refetch: reloadPros,
    } = useQuery({
        queryKey: ['calendar', 'pros'],
        queryFn: async () =>
            (await RecordFactory.getInstance().loadPayees(PAYEE_TYPE.PAYEE_TYPE_ANY)) || [],
    })

    const {
        data: bookablesByDate,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ['calendar', 'bookables', currentDate, currentView, filter],
        queryFn: async ({ signal }) => {
            const { start, end } = getCalendarStartAndEnd(currentDate, currentView)

            const result = await RecordFactory.getInstance().fetchCalendar(
                start,
                end,
                undefined,
                undefined,
                filter,
                signal
            )

            if (!result.ok) throw result.error

            await Promise.all([reloadItems(), reloadPros()])

            return getBookablesByDate(result.value)
        },
        onError: (error) => {
            if (!axios.isCancel(error)) {
                logAndDisplayError(error)
            }
        },
    })

    useNotificationListener('ScheduleDataSourceChanged', () => refetch())

    const [searchText, setSearchText] = useState('')
    const debouncedSearchText = useDebounced(searchText, 250)

    const {
        data: searchResults,
        isFetching: isSearching,
        isSuccess: isSearchSuccess,
    } = useQuery({
        queryKey: ['calendar', 'bookables', 'search', debouncedSearchText],
        queryFn: async ({ signal }) => {
            if (!searchText) return {}

            const result = await RecordFactory.getInstance().searchCalendar(
                searchText,
                undefined,
                undefined,
                signal
            )

            if (!result.ok) throw result.error

            await Promise.all([reloadItems(), reloadPros()])

            return getBookablesByDate(result.value)
        },
        enabled: !!searchText,
        staleTime: 30000, // Consider results fresh for 30 seconds
        keepPreviousData: true,
        retry: false,
        onError: (error) => {
            if (!axios.isCancel(error)) {
                logAndDisplayError(error)
            }
        },
    })

    const goToDay = (day: moment.Moment) => {
        setCurrentView('day')
        setCurrentDate(day)
    }

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
        useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        })
    )

    const { mutateAsync: changeBookingDate } = useMutation({
        mutationFn: async ({
            bookable,
            date,
            notify,
            changeAll,
        }: {
            bookable: Bookable
            date: moment.Moment
            notify: boolean
            changeAll: boolean
        }) => {
            if (
                !(
                    bookable instanceof Booking ||
                    bookable instanceof Lesson ||
                    bookable instanceof Order ||
                    bookable instanceof Block ||
                    bookable instanceof Task
                )
            )
                return

            const clone = bookable.clone()
            clone.date = date

            let result: Result<Bookable | Payment | string | null> = okResult(null)

            if (clone instanceof Booking)
                result = await RecordFactory.getInstance().changeBooking(clone, !notify, changeAll)
            else if (clone instanceof Lesson)
                result = await RecordFactory.getInstance().sendClass(
                    clone,
                    clone.schedule,
                    undefined,
                    notify,
                    changeAll
                )
            else if (clone instanceof Order)
                result = await RecordFactory.getInstance().sendOrder(clone, !notify)
            else if (clone instanceof Block)
                result = await RecordFactory.getInstance().sendBlock(clone, changeAll)
            else if (clone instanceof Task)
                result = await RecordFactory.getInstance().sendTask(clone)

            if (!result.ok) throw result.error

            return result.value
        },
        onError: (error) => {
            logAndDisplayError(error)
        },
    })

    const [showSnack, setShowSnack] = useState(false)
    const [snackTimeout, setSnackTimeout] = useState<NodeJS.Timeout | null>(null)

    const closeSnackbar = () => {
        if (!snackTimeout) return
        clearTimeout(snackTimeout)
        setSnackTimeout(null)
        setShowSnack(false)
    }

    const changeDateForBookable = async (
        bookable?: Bookable,
        date?: moment.Moment,
        notify = false,
        changeAll = false
    ) => {
        if (!bookable || !date) return
        // TODO: show any conflicts - blocked by schedule-client/availability port
        await changeBookingDate({ bookable, date, notify, changeAll })

        setShowSnack(true)

        if (snackTimeout) {
            clearTimeout(snackTimeout)
        }

        setSnackTimeout(
            setTimeout(() => {
                setShowSnack(false)
                setSnackTimeout(null)
            }, 3000)
        )
    }

    const handleBannerClose = () => {
        sessionStorage.setItem(SESSION_STORAGE_DISMISS_CALENDAR_BANNER, 'true')
        setShowBanner(false)
    }

    const isLoading = isFetching || isLoadingItems || isLoadingPros || isSearching

    const heightOffset = useMemo(() => {
        let offset = HEADER_BAR_HEIGHT + CALENDAR_MARGIN + CALENDAR_PADDING
        offset += showBanner ? DASHBOARD_BANNER_HEIGHT + DASHBOARD_BANNER_MARGIN : 0
        offset += !filter.isEmpty ? FILTER_ROW_HEIGHT + FILTER_ROW_MARGIN : 0
        return offset
    }, [showBanner, filter])

    return (
        <div className="h-screen overflow-y-auto">
            <div className="relative m-8 mb-0 h-[calc(100%-32px)]">
                {showBanner && (
                    <div className="mx-auto mb-6 w-[680px]">
                        <Banner
                            text={dashboard?.title ? dashboard.title : 'Sync your calendar'}
                            subtitle={
                                dashboard?.status
                                    ? dashboard.status
                                    : 'Keep track of personal events'
                            }
                            buttonText={!dashboard ? 'Connect' : 'Update'}
                            icon={!dashboard ? 'calendar.large' : 'refresh.large'}
                            intent={!dashboard ? 'info' : 'danger'}
                            href="/settings/calendar/sync"
                            onClose={handleBannerClose}
                        />
                    </div>
                )}
                <CalendarHeaderBar
                    view={currentView}
                    date={currentDate}
                    pros={pros ?? []}
                    items={items ?? []}
                    filter={filter}
                    availabilityFilter={availabilityFilter}
                    searchText={searchText}
                    setDate={(d) => setCurrentDate(d)}
                    setView={(v) => setCurrentView(v)}
                    onUpdateFilter={(f) => setFilter(f)}
                    onSearch={setSearchText}
                    onClearSearch={() => setSearchText('')}
                />
                {!filter.isEmpty && currentView !== 'availability' && (
                    <CalendarFilterTags
                        filter={filter}
                        pros={pros ?? []}
                        items={items ?? []}
                        onRemove={(f) => setFilter(f)}
                    />
                )}
                {isLoading && (
                    <div
                        className="flex items-center justify-center"
                        style={{ height: `calc(100% - ${heightOffset}px)` }}
                    >
                        <Spinner />
                    </div>
                )}
                {!isLoading && (
                    <>
                        {currentView === 'availability' && (
                            <CalendarAvailability
                                currentDate={currentDate}
                                items={items ?? []}
                                pros={pros ?? []}
                                availabilityFilter={availabilityFilter}
                            />
                        )}
                        {currentView === 'agenda' && (
                            <CalendarAgenda
                                heightOffset={heightOffset}
                                currentDate={currentDate}
                                pros={pros ?? []}
                                filter={filter}
                                bookablesByDate={bookablesByDate ?? {}}
                            />
                        )}
                        {currentView === 'month' && (
                            <CalendarMonth
                                heightOffset={heightOffset}
                                currentDate={currentDate}
                                pros={pros ?? []}
                                filter={filter}
                                bookablesByDate={bookablesByDate ?? {}}
                                showBanner={showBanner}
                                onGoToDay={goToDay}
                            />
                        )}
                        {['week', '4day'].includes(currentView) && (
                            <CalendarWeek
                                heightOffset={heightOffset}
                                currentView={currentView}
                                currentDate={currentDate}
                                pros={pros ?? []}
                                bookablesByDate={bookablesByDate ?? {}}
                                onGoToDay={goToDay}
                            />
                        )}
                        {currentView === 'day' && (
                            <DndContext
                                sensors={sensors}
                                modifiers={[restrictToFirstScrollableAncestor]}
                            >
                                <CalendarDay
                                    heightOffset={heightOffset}
                                    currentDate={currentDate}
                                    pros={pros ?? []}
                                    filter={filter}
                                    bookablesByDate={bookablesByDate ?? {}}
                                    onChangeTime={changeDateForBookable}
                                />
                            </DndContext>
                        )}
                        {currentView === 'search' && !!searchText && !!isSearchSuccess && (
                            <CalendarSearch
                                heightOffset={heightOffset}
                                pros={pros ?? []}
                                filter={filter}
                                searchResults={!!searchResults ? searchResults : {}}
                            />
                        )}
                    </>
                )}
                {showSnack && (
                    // TODO: refactor into reusable Snackbar UI component
                    <div className="absolute bottom-10 left-1/2 flex translate-x-[-50%] items-center gap-4 rounded-lg bg-gray-700 p-1 pl-4 text-white shadow-lg">
                        <Icon icon="checkmark.large" shouldInheritColor />
                        <Paragraph>Time changed</Paragraph>
                        <Button
                            state="dark"
                            shape="square"
                            intent="tertiary_deprecated"
                            onClick={closeSnackbar}
                        >
                            <Icon icon="close.medium" shouldInheritColor />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}
